import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { 
  User, Mail, Phone, Globe, CheckCircle, 
  Clock, Calendar, MapPin, Users, TrendingUp, DollarSign,
  Home, Briefcase, Heart, Star, Edit3, Save, X, Eye, EyeOff, ChevronLeft, Building
} from 'lucide-react';
import ProfileLoading from './ProfileLoading';
import ProfileError from './ProfileError';
import PasswordChangeCard from './PasswordChangeCard';

import ProfileHeader from './ProfileHeader';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import './ModernProfileContainer.css';

const ModernProfileContainer = () => {
  const navigate = useNavigate();
  const { user: authUser, loading: authLoading, updateProfile } = useAuth();
  const { currentThemeName, theme } = useTheme();
  const { t } = useLanguage();
  
  // Get translated business options
  const getBusinessOptions = () => [
    t('business.beautySalon'),
    t('business.restaurant'),
    t('business.retail'),
    t('business.coffeeShop'),
    t('business.barbershop'),
    t('business.bakery'),
    t('business.gym'),
    t('business.autoRepair'),
    t('business.laundry'),
    t('business.petGrooming'),
    t('business.tattooParlor'),
    t('business.nailSalon'),
    t('business.photographyStudio'),
    t('business.consulting'),
    t('business.daycareCenter'),
    t('business.cleaningService'),
    t('business.foodTruck'),
    t('business.yogaStudio'),
    t('business.musicSchool'),
    t('business.other')
  ];
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // const [autoSaveTimer, setAutoSaveTimer] = useState(null); // Auto-save disabled
  
  // Optimized input handler for smooth typing
  const handleInputChange = useCallback((newValue) => {
    setEditValue(newValue);
  }, []);

  // Responsive design
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mobile-specific optimizations
  useEffect(() => {
    if (isMobile) {
      // Add mobile-specific body class for CSS targeting
      document.body.classList.add('mobile-profile-view');
      
      // Add theme-specific body class
      if (currentThemeName) {
        document.body.classList.add(`theme-${currentThemeName}`);
      }
      
      // Optimize touch interactions
      const style = document.createElement('style');
      style.textContent = `
        .mobile-profile-view .modern-profile-container {
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }
        
        .mobile-profile-view .modern-profile-container button {
          -webkit-tap-highlight-color: transparent;
        }
        
        .mobile-profile-view .modern-profile-container input {
          -webkit-appearance: none;
          border-radius: 8px;
        }
        
        .mobile-profile-view .profile-page-header {
          background: rgba(255, 255, 255, 0.95) !important;
          backdrop-filter: blur(10px) !important;
        }
        
        .mobile-profile-view .profile-page-header .header-center {
          display: none !important;
        }
        
        .mobile-profile-view .profile-page-header .header-right {
          width: auto !important;
          gap: 8px !important;
        }
        
        /* Theme-specific mobile styles */
        .mobile-profile-view.theme-dark .profile-page-header {
          background: rgba(15, 23, 42, 0.95) !important;
        }
        
        .mobile-profile-view.theme-beige .profile-page-header {
          background: rgba(245, 245, 220, 0.95) !important;
        }
      `;
      document.head.appendChild(style);
      
      return () => {
        document.body.classList.remove('mobile-profile-view');
        if (currentThemeName) {
          document.body.classList.remove(`theme-${currentThemeName}`);
        }
        document.head.removeChild(style);
      };
    }
  }, [isMobile, currentThemeName]);

  // Get theme colors based on current theme
  const getThemeColors = () => {
    switch (currentThemeName) {
      case 'light':
        return {
          background: '#ffffff',
          cardBg: '#f8fafc',
          text: '#1e293b',
          textSecondary: '#64748b',
          primary: '#3b82f6',
          primaryBorder: '#e2e8f0',
          cardBorder: '#e2e8f0',
          secondaryBorder: '#cbd5e1',
          cardHoverBg: '#f1f5f9',
          success: '#10b981',
          warning: '#f59e0b',
          error: '#ef4444'
        };
      case 'dark':
        return {
          background: '#0f172a',
          cardBg: '#1e293b',
          text: '#f1f5f9',
          textSecondary: '#94a3b8',
          primary: '#60a5fa',
          primaryBorder: '#334155',
          cardBorder: '#475569',
          secondaryBorder: '#64748b',
          cardHoverBg: '#334155',
          success: '#22c55e',
          warning: '#fbbf24',
          error: '#f87171'
        };
      case 'beige':
        return {
          background: '#f5f5dc',
          cardBg: '#fafaf0',
          text: '#8b4513',
          textSecondary: '#a0522d',
          primary: '#8b4513',
          primaryBorder: '#d2b48c',
          cardBorder: '#deb887',
          secondaryBorder: '#f4a460',
          cardHoverBg: '#f0f0d8',
          success: '#228b22',
          warning: '#daa520',
          error: '#dc143c'
        };
      default:
        return {
          background: '#ffffff',
          cardBg: '#f8fafc',
          text: '#1e293b',
          textSecondary: '#64748b',
          primary: '#3b82f6',
          primaryBorder: '#e2e8f0',
          cardBorder: '#e2e8f0',
          secondaryBorder: '#cbd5e1',
          cardHoverBg: '#f1f5f9',
          success: '#10b981',
          warning: '#f59e0b',
          error: '#ef4444'
        };
    }
  };

  const themeColors = getThemeColors();

  // Get theme styles for components
  const getThemeStyles = () => {
    const baseStyles = {
      cardBg: themeColors.cardBg,
      border: themeColors.primaryBorder,
      text: themeColors.text,
      textSecondary: themeColors.textSecondary,
      primary: themeColors.primary,
      success: themeColors.success,
      warning: themeColors.warning
    };
    return baseStyles;
  };

  const themeStyles = getThemeStyles();

  // Function to fetch fresh user data from backend
  const fetchUserProfile = async () => {
    try {
          const response = await fetch(`${getApiBaseUrl()}/api/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const userData = await response.json();
        console.log('🔄 Fetched fresh user data from backend:', userData.user);
        setUser(userData.user);
        // Also update the AuthContext user data
        localStorage.setItem('user', JSON.stringify(userData.user));
      } else {
        console.error('Failed to fetch user profile:', response.status);
        // Fallback to authUser if fetch fails
        if (authUser) {
          setUser(authUser);
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Fallback to authUser if fetch fails
      if (authUser) {
        setUser(authUser);
      }
    }
  };

  // Load user data
  useEffect(() => {
    console.log('🔄 ModernProfileContainer useEffect - authLoading:', authLoading, 'authUser:', authUser);
    
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (authUser) {
      console.log('🔄 Setting user from authUser:', authUser);
      setUser(authUser);
      setLoading(false);
      
      // Fetch fresh data from backend to ensure we have the latest business info
      fetchUserProfile();
    } else {
      console.log('🔄 No authUser, setting user to null');
      setUser(null);
      setLoading(false);
    }
  }, [authUser, authLoading]);

  // Auto-save functionality - DISABLED
  // useEffect(() => {
  //   if (autoSaveTimer) {
  //     clearTimeout(autoSaveTimer);
  //   }

  //   if (editingField && editValue !== (user?.[editingField] || '')) {
  //     const timer = setTimeout(() => {
  //       handleSave(editingField);
  //     }, 2000); // Auto-save after 2 seconds of inactivity
  //     
  //     setAutoSaveTimer(timer);
  //   }

  //   return () => {
  //     if (autoSaveTimer) {
  //       clearTimeout(autoSaveTimer);
  //     }
  //   };
  // }, [editValue, editingField]);

  const handleEdit = (fieldName, currentValue) => {
    // Optimize edit handling to prevent unnecessary re-renders
    if (editingField !== fieldName) {
      setEditingField(fieldName);
      setEditValue(currentValue || '');
    }
  };

  const handleSave = async (fieldName) => {
    // Ensure we have a field name to update
    const fieldToUpdate = fieldName || editingField;
    if (!fieldToUpdate) {
      console.error('❌ No field name provided for update');
      showNotification('Update failed: No field specified', 'error');
      return;
    }
    
    // Debug: Check if updateProfile function is available
    console.log('🔍 Debug: updateProfile function available:', !!updateProfile);
    console.log('🔍 Debug: updateProfile function type:', typeof updateProfile);
    console.log('🔍 Debug: authUser available:', !!authUser);
    console.log('🔍 Debug: authLoading:', authLoading);
    
    // Allow empty values - don't trim or validate the content
    // Users should be able to clear fields if they want
    console.log('🔍 About to save field:', fieldToUpdate, 'with value:', editValue);
    
    if (!updateProfile) {
      console.error('❌ updateProfile function is not available');
      showNotification('Update failed: Profile update service not available', 'error');
      return;
    }
    
    setSaving(true);
    try {
      console.log('🔍 Profile update debug info:');
      console.log('Field being updated:', fieldToUpdate);
      console.log('New value:', editValue);
      console.log('Current user:', user);
      console.log('Auth context user:', authUser);
      
      // Create the update payload - allow empty values for clearing fields
      const updatePayload = { [fieldToUpdate]: editValue };
      console.log('📤 Update payload being sent:', updatePayload);
      
      console.log('🔍 Calling updateProfile function...');
      const result = await updateProfile(updatePayload);
      
      console.log('📝 Update result:', result);
      
      if (result.success) {
        console.log('✅ Profile update successful, updating local state with:', result.user);
        setUser(result.user);
        setEditingField(null);
        setEditValue('');
        
        // Force refresh from AuthContext to ensure consistency
        if (authUser && JSON.stringify(authUser) !== JSON.stringify(result.user)) {
          console.log('🔄 AuthContext user differs from result, forcing refresh...');
          // The AuthContext should have been updated, so we can sync here
          setUser(result.user);
        }
        
        // Show success feedback
        showNotification('Profile updated successfully!', 'success');
      } else {
        console.error('❌ Failed to update field:', result.error);
        showNotification(`Update failed: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('💥 Error updating field:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      showNotification(`An error occurred: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditValue('');
    // if (autoSaveTimer) {
    //   clearTimeout(autoSaveTimer);
    // }
  };

  // Remove the handlePasswordChange function since it's no longer needed
  // const handlePasswordChange = async (passwordData) => {
  //   try {
  //     const result = await changePassword(passwordData);
  //     return result;
  //   } catch (error) {
  //     console.error('Error changing password:', error);
  //     return { success: false, error: error.message };
  //   }
  // };

  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      border-radius: 8px;
      color: white;
      font-family: Inter, -apple-system, BlinkMacSystemFont, sans-serif;
      font-size: 14px;
      font-weight: 500;
      z-index: 10000;
      transform: translateX(100%);
      transition: transform 0.3s ease;
      background: ${type === 'success' ? '#22c55e' : type === 'error' ? '#ef4444' : '#3b82f6'};
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  };

  if (loading) {
    return <ProfileLoading isMobile={isMobile} />;
  }

  if (!user) {
    return <ProfileError isMobile={isMobile} />;
  }

  const ProfileCard = ({ title, icon: Icon, color, children, className = '' }) => (
    <div 
      className={`profile-card ${className} ${saving ? 'saving' : ''}`}
      style={{
        background: themeStyles.cardBg,
        border: `1px solid ${themeStyles.border}`,
        borderRadius: 16,
        padding: 24,
        marginBottom: 20,
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(8px)',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 8px 25px rgba(0, 0, 0, 0.1), 0 0 0 1px ${color}20`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Background gradient overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, ${color}05 0%, ${color}02 100%)`,
        pointerEvents: 'none'
      }} />
      
      {/* Card Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 20,
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 48,
          borderRadius: 12,
          background: `${color}15`,
          color: color,
          border: `1px solid ${color}30`
        }}>
          <Icon size={24} />
        </div>
        <div>
          <h3 style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 600,
            color: themeStyles.text
          }}>
            {title}
          </h3>
        </div>
      </div>

      {/* Card Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </div>
  );

  const EditableField = ({ label, fieldName, value, icon: Icon, color, type = 'text', required = false, options = [] }) => {
    const isEditing = editingField === fieldName;
    const currentValue = value || '';
    const displayValue = currentValue || t('common.notSet');

    const getInputType = () => {
      if (type === 'password') return showPassword ? 'text' : 'password';
      if (type === 'email') return 'email';
      if (type === 'tel') return 'tel';
      if (type === 'url') return 'url';
      return 'text';
    };

    return (
      <div className="editable-field" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 0',
        borderBottom: `1px solid ${themeStyles.border}40`,
        position: 'relative'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flex: 1
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 8,
            background: `${color}15`,
            color: color
          }}>
            <Icon size={16} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: 14,
              fontWeight: 500,
              color: themeStyles.textSecondary,
              marginBottom: 4
            }}>
              {label}
              {required && <span style={{ color: themeColors.error, marginLeft: 4 }}>*</span>}
            </div>
            {isEditing ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                {type === 'select' ? (
                  <select
                    value={editValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      // Handle Enter key for quick save
                      if (e.key === 'Enter') {
                        handleSave(fieldName);
                      }
                      // Handle Escape key for cancel
                      if (e.key === 'Escape') {
                        handleCancel();
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: `1px solid ${themeStyles.border}`,
                      borderRadius: 6,
                      fontSize: 14,
                      background: currentThemeName === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'white',
                      color: themeStyles.text,
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      cursor: 'pointer'
                    }}
                    autoFocus
                  >
                    <option value="">Select {label.toLowerCase()}</option>
                    {options.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={getInputType()}
                    value={editValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      // Handle Enter key for quick save
                      if (e.key === 'Enter') {
                        handleSave(fieldName);
                      }
                      // Handle Escape key for cancel
                      if (e.key === 'Escape') {
                        handleCancel();
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: `1px solid ${themeStyles.border}`,
                      borderRadius: 6,
                      fontSize: 14,
                      background: currentThemeName === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'white',
                      color: themeStyles.text,
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    placeholder={t('common.enterField', { field: label.toLowerCase() })}
                    autoFocus
                  />
                )}
                {type === 'password' && (
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      padding: '8px',
                      border: 'none',
                      background: 'transparent',
                      color: themeStyles.textSecondary,
                      cursor: 'pointer',
                      borderRadius: 4
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                )}
              </div>
            ) : (
              <div style={{
                fontSize: 16,
                color: themeStyles.text,
                fontWeight: 500
              }}>
                {displayValue}
              </div>
            )}
          </div>
        </div>
        
        {!isEditing ? (
          <button
            className="edit-button"
            onClick={() => handleEdit(fieldName, currentValue)}
            style={{
              padding: '8px',
              border: 'none',
              background: 'transparent',
              color: themeStyles.textSecondary,
              cursor: 'pointer',
              borderRadius: 6,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${color}15`;
              e.currentTarget.style.color = color;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = themeStyles.textSecondary;
            }}
          >
            <Edit3 size={16} />
          </button>
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}>
            <button
              className="save-button"
              onClick={() => handleSave(fieldName)}
              disabled={saving}
              style={{
                padding: '8px',
                border: 'none',
                background: themeColors.success,
                color: 'white',
                cursor: 'pointer',
                borderRadius: 6,
                transition: 'all 0.2s ease',
                opacity: saving ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Save size={16} />
            </button>
            <button
              className="cancel-button"
              onClick={handleCancel}
              style={{
                padding: '8px',
                border: 'none',
                background: themeColors.error,
                color: 'white',
                cursor: 'pointer',
                borderRadius: 6,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      className={`modern-profile-container ${currentThemeName ? `${currentThemeName}-theme` : 'white-theme'}`}
      data-theme={currentThemeName || 'white'}
      style={{
        minHeight: '100vh',
        background: themeColors.background,
        color: themeColors.text,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        padding: '20px',
        paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
        paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {/* Profile Header Component */}
      <ProfileHeader 
        user={user} 
        isMobile={isMobile} 
        currentThemeName={currentThemeName} 
      />

      {/* Profile Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        marginTop: '32px'
      }}>
        {/* Page Title Section */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          marginBottom: 32
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '24px 0',
            borderBottom: `1px solid ${themeColors.border}40`
          }}>
            <div className="profile-page-title-section">
              <h1 className="profile-page-title" style={{
                fontSize: '42px',
                fontWeight: '700',
                margin: '0 0 8px 0',
                background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {t('profile.title')}
              </h1>
              <p className="profile-page-subtitle" style={{
                fontSize: '18px',
                color: themeColors.textSecondary,
                margin: '0 0 16px 0'
              }}>
                {t('profile.subtitle')}
              </p>
            </div>

          </div>
        </div>

        {/* Profile Header Section */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          marginBottom: 32
        }}>
          <div className="profile-header" style={{
            background: themeColors.cardBg,
            border: `1px solid ${themeColors.border}`,
            borderRadius: 20,
            padding: 32,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Background pattern */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(circle at 20% 80%, ${themeColors.primary}10 0%, transparent 50%),
                          radial-gradient(circle at 80% 20%, ${themeColors.success}10 0%, transparent 50%)`,
              pointerEvents: 'none'
            }} />
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div className="profile-avatar" style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${themeColors.primary}20, ${themeColors.success}20)`,
                border: `3px solid ${themeColors.primary}`,
                margin: '0 auto 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 32
              }}>
                {user.first_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
              </div>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: 28,
                fontWeight: 700,
                color: themeColors.text,
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }} title={user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.first_name || user.email || ''}>
                {user.first_name && user.last_name 
                  ? `${user.first_name} ${user.last_name}` 
                  : user.first_name || user.email || 'User Profile'
                }
              </h2>
              <p style={{
                margin: 0,
                fontSize: 16,
                color: themeColors.textSecondary,
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }} title={user.email || ''}>
                {user.email || 'No email provided'}
              </p>
            </div>
          </div>
        </div>

        {/* Left Column */}
        <div>
          <ProfileCard title={t('profile.personalInfo')} icon={User} color={themeStyles.primary}>
            <EditableField 
              label={t('auth.firstName')} 
              fieldName="firstName" 
              value={user.first_name} 
              icon={User} 
              color={themeStyles.primary}
              required
            />
            <EditableField 
              label={t('auth.lastName')} 
              fieldName="lastName" 
              value={user.last_name} 
              icon={User} 
              color={themeColors.success}
              required
            />
            <EditableField 
              label={t('auth.email')} 
              fieldName="email" 
              value={user.email} 
              icon={Mail} 
              color={themeColors.warning}
              type="email"
              required
            />
            <EditableField 
              label={t('auth.phone')} 
              fieldName="phone" 
              value={user.phone} 
              icon={Phone} 
              color={themeStyles.primary}
              type="tel"
            />
          </ProfileCard>

          {/* Password Change Card */}
          <PasswordChangeCard 
            themeColors={themeColors}
            themeStyles={themeStyles}
            currentThemeName={currentThemeName}
          />

        </div>

        {/* Right Column */}
        <div>
          <ProfileCard title={t('profile.contactLocation')} icon={MapPin} color={themeColors.success}>
            <EditableField 
              label={t('auth.website')} 
              fieldName="website" 
              value={user.website} 
              icon={Globe} 
              color={themeStyles.primary}
              type="url"
            />
            <EditableField 
              label={t('auth.address')} 
              fieldName="addressLine1" 
              value={user.address_line1} 
              icon={Home} 
              color={themeColors.success}
            />
            <EditableField 
              label={t('auth.city')} 
              fieldName="city" 
              value={user.city} 
              icon={MapPin} 
              color={themeColors.warning}
            />
            <EditableField 
              label={t('auth.state')} 
              fieldName="state" 
              value={user.state} 
              icon={MapPin} 
              color={themeStyles.primary}
            />
            <EditableField 
              label={t('auth.zipCode')} 
              fieldName="zipCode" 
              value={user.zipCode} 
              icon={MapPin} 
              color={themeColors.info}
            />
          </ProfileCard>

          <ProfileCard title={t('profile.businessInfo')} icon={Briefcase} color={themeStyles.primary}>
            <EditableField 
              label={t('business.businessType')} 
              fieldName="business_type" 
              value={user.business_type} 
              icon={Briefcase} 
              color={themeStyles.primary}
              type="select"
              options={getBusinessOptions()}
            />
            <EditableField 
              label={t('business.secondBusinessType')} 
              fieldName="second_business_type" 
              value={user.second_business_type || ''} 
              icon={Briefcase} 
              color={themeStyles.secondary}
              type="select"
              options={[
                '',
                'Beauty Salon',
                'Restaurant',
                'Retail Store',
                'Coffee Shop',
                'Barbershop',
                'Bakery',
                'Gym',
                'Auto Repair',
                'Laundry Service',
                'Pet Grooming',
                'Tattoo Parlor',
                'Nail Salon',
                'Photography Studio',
                'Consulting',
                'Daycare Center',
                'Cleaning Service',
                'Food Truck',
                'Yoga Studio',
                'Music School',
                'Dance Studio',
                'Art Gallery',
                'Bookstore',
                'Florist Shop',
                'Jewelry Store',
                'Vintage Shop',
                'Tech Startup',
                'Dental Office',
                'Law Firm',
                'Real Estate',
                'Insurance Agency',
                'Accounting Firm',
                'Marketing Agency',
                'Web Design',
                'Construction',
                'Landscaping',
                'Plumbing',
                'Electrical',
                'HVAC',
                'Roofing',
                'Painting',
                'Carpentry',
                'Other'
              ]}
              placeholder="Select second business type (optional)"
            />
            <EditableField 
              label={t('business.businessName')} 
              fieldName="business_name" 
              value={user.business_name} 
              icon={Building} 
              color={themeColors.success}
            />
            <EditableField 
              label={t('business.corporationType')} 
              fieldName="corporation_type" 
              value={user.corporation_type} 
              icon={Building} 
              color={themeColors.warning}
              type="select"
              options={[
                'Sole Proprietorship',
                'Partnership',
                'LLC',
                'Corporation (C-Corp)',
                'S-Corporation',
                'Non-profit',
                'Other'
              ]}
            />
            <EditableField 
              label={t('business.employeeCount')} 
              fieldName="employee_count" 
              value={user.employee_count} 
              icon={Users} 
              color={themeStyles.primary}
              type="select"
              options={[
                '1-5 employees',
                '6-10 employees',
                '11-25 employees',
                '26-50 employees',
                '51-100 employees',
                '100+ employees'
              ]}
            />
            <EditableField 
              label={t('business.yearsInBusiness')} 
              fieldName="years_in_business" 
              value={user.years_in_business} 
              icon={Calendar} 
              color={themeColors.success}
              type="select"
              options={[
                'Less than 1 year',
                '1-2 years',
                '3-5 years',
                '6-10 years',
                '11-20 years',
                '20+ years'
              ]}
            />
          </ProfileCard>

          <ProfileCard title={t('profile.professional')} icon={TrendingUp} color={themeStyles.primary}>
            <EditableField 
              label={t('business.industry')} 
              fieldName="industry" 
              value={user.industry} 
              icon={TrendingUp} 
              color={themeStyles.primary}
            />
          </ProfileCard>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        maxWidth: '1200px',
        margin: '32px auto 0',
        textAlign: 'center',
        color: themeStyles.textSecondary,
        fontSize: 14
      }}>
        <div style={{
          padding: '20px',
          borderTop: `1px solid ${themeStyles.border}40`
        }}>
          <p style={{ margin: '0 0 8px 0' }}>
            {t('profile.securePrivatePersonalized')}
          </p>
          <p style={{ margin: 0, fontSize: 12 }}>
            {t('common.lastUpdated')}: {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>


    </div>
  );
};

export default ModernProfileContainer;
